import React, { useMemo } from 'react';
import { Column, Row, Title } from '@ydistri/ds';
import ScopeTypeSelect from './ScopeTypeSelect';
import type { MultiselectCustomerPartialEntity } from '../../../components/global/ConfigurationMultiselect/configurationMultiselectTypes';
import {
  configurationRuleScopeModeColor,
  configurationRuleScopeModeTitle,
  getScopeStoreAvailabilityWarningText,
} from './scopeLib';
import ScopeConfigurationMultiselect from './ScopeConfigurationMultiselect';
import ScopeMultiselectCounter from './ScopeMultiselectCounter';
import ScopeViewMode from './ScopeViewMode';
import { ConfigurationRuleScopeEntityType, ConfigurationRuleScopeMode } from '@ydistri/api-sdk';
import { useScope } from './useScope';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../store';
import { Tag } from 'antd';
import { styled } from 'styled-components';
import { useOpenScopeModal } from './useOpenScopeModal';
import { computeRemSize } from '@ydistri/theme';
import {
  isMainScope,
  isModalViewMode,
  isNonModalViewMode,
  scopeEntityTitle,
  ViewMode,
} from './scopeUtilsLib';
import { TemplateOrCalculationWithValue } from '../../../apis/api';
import { useStoresReport } from './useStoresReport';
import { missingEntities, noStoresIds } from './storesReportLib';

const SelectionOptionTag = styled(Tag)<{ $disabled: boolean }>`
  width: 80px;
  cursor: ${p => (p.$disabled ? 'initial' : 'pointer')};
  margin: ${computeRemSize(3)};
`;

interface ScopeEntitySelectionProps {
  scopeId: number;
  entityType: ConfigurationRuleScopeEntityType;
  options: MultiselectCustomerPartialEntity[];
  dataCheck?: MultiselectCustomerPartialEntity[];
  dataCheckLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  viewMode: ViewMode;
  templateOrCalculation: TemplateOrCalculationWithValue;
}

const ScopeEntitySelection: React.FC<ScopeEntitySelectionProps> = ({
  scopeId,
  entityType,
  options,
  dataCheck,
  dataCheckLabel,
  loading,
  disabled = false,
  viewMode,
  templateOrCalculation,
}) => {
  const entityTitle = scopeEntityTitle[entityType];
  const scopeById = useScope(scopeId, templateOrCalculation);
  const modalScope = useSelector((state: ReduxState) => state.scope.modalScope);
  const { problems } = useStoresReport(templateOrCalculation);

  const scope = useMemo(
    () => (isModalViewMode(viewMode) ? modalScope : scopeById),
    [modalScope, scopeById, viewMode],
  );

  const openModalHandler = useOpenScopeModal(scope);

  const selectionOption = scope?.entities[entityType].selectionOption;
  const selectedIds = scope?.entities[entityType].selectedIds;

  // The regions and store types the server says have no store left after the general scope (B9).
  const tagWarnings = useMemo((): Record<number, React.ReactNode | undefined> => {
    const warningText = getScopeStoreAvailabilityWarningText(true, entityType);
    return noStoresIds(problems, scopeId)[entityType].reduce<
      Record<number, React.ReactNode | undefined>
    >((warnings, id) => {
      warnings[id] = warningText;
      return warnings;
    }, {});
  }, [entityType, problems, scopeId]);

  // The selected ids the server says the general scope filtered out (B11): red tags.
  const missingIds = useMemo(
    () => missingEntities(problems, scopeId).ids[entityType],
    [entityType, problems, scopeId],
  );

  if (
    selectionOption === ConfigurationRuleScopeMode.All &&
    viewMode === ViewMode.EXCEPTION &&
    !isMainScope(scope)
  )
    return null;
  // && !isModal

  return (
    <Row $gap="1rem" $width="100%" $flexWrap="nowrap">
      <Row $width="230px" $minWidth="230px" $justifyContent="space-between">
        <Title $size="small">{entityTitle}</Title>
        {isNonModalViewMode(viewMode) && selectionOption && (
          <SelectionOptionTag
            onClick={!disabled ? openModalHandler : undefined}
            color={configurationRuleScopeModeColor[selectionOption]}
            $disabled={disabled}
          >
            {configurationRuleScopeModeTitle[selectionOption]}
          </SelectionOptionTag>
        )}
      </Row>
      {isNonModalViewMode(viewMode) && (
        <ScopeViewMode
          scopeId={scopeId}
          entityType={entityType}
          options={options}
          disabled={disabled}
          tagWarnings={tagWarnings}
          missingIds={missingIds}
          templateOrCalculation={templateOrCalculation}
        />
      )}
      {isModalViewMode(viewMode) && (
        <Column $gap="1rem" $flexGrow={1} $alignItems="end">
          {selectionOption && (
            <ScopeTypeSelect
              playground={isMainScope(scope)}
              selectionOption={selectionOption}
              entityType={entityType}
            />
          )}
          {selectionOption !== ConfigurationRuleScopeMode.All && (
            <ScopeConfigurationMultiselect
              selectedIds={selectedIds}
              entityType={entityType}
              data={options}
              dataCheck={dataCheck}
              dataCheckLabel={dataCheckLabel}
              loading={loading}
              tagWarnings={tagWarnings}
              missingIds={missingIds}
            />
          )}
          {selectionOption !== ConfigurationRuleScopeMode.All && (
            <ScopeMultiselectCounter selectedIds={selectedIds} entityType={entityType} />
          )}
        </Column>
      )}
    </Row>
  );
};

export default ScopeEntitySelection;
